/**
 * useScoreDragSelection
 *
 * Rubber-Band-Mehrfachauswahl: Klick-Halten-Ziehen zieht ein Auswahlrechteck auf; beim Loslassen
 * werden alle druckbaren Noten selektiert, deren Notenkopf-Mittelpunkt im Rechteck liegt.
 * Sub-Composable von {@link @/composables/useOsmdInteractiveScore}.
 *
 * **Nur die Geste lebt hier – die Auswahl-Mutation bleibt exklusiv in `useScoreSelection`
 * (kritisch – nicht rückbauen):** dieses Composable berechnet Rechteck + Treffer und ruft beim
 * Loslassen genau EINMAL `applyDragSelection`. So bleiben die Selektions-Invarianten (eine
 * `selectedNotes`-Zuweisung pro Geste wegen des `flush:'sync'`-Watches, Anker-Pflege,
 * `actionScope`, Stummheit) an ihrem einzigen Ort. Pro Drag-Frame wird ausschließlich der
 * transiente **Hover-Layer** gefärbt (`highlightHoverNotes`) – die bestehende Auswahl behält
 * dabei ihre Auswahl-Farbe, ein additiver Drag (Strg/Cmd) zeigt die Vereinigung also von selbst.
 *
 * **Pointer Events + Pointer Capture** statt window-`mousemove`/`mouseup`-Listenern: die Capture
 * routet `pointermove`/`pointerup` auch außerhalb von Container und Fenster hierher, und
 * `pointercancel` liefert den Abbruchpfad gratis. Bewusst KEIN `preventDefault`/`stopPropagation`
 * am `pointerdown`: die Kompatibilitäts-Mausevents (Hover) und der window-`pointerdown` des
 * Upload-Overlay-Fixes (`shared/windowFileDrag.ts`, setzt nur seine Flagge zurück) müssen
 * weiterlaufen; das Schlucken des Folge-`click` nach einem Drag läuft deshalb über die explizite
 * One-Shot-Flagge {@link consumeDragClick}, nicht über Event-Cancelling. Dass während der Geste
 * kein nativer HTML5-Drag startet, garantiert `@dragstart.prevent` am Container
 * (`MusicXmlPreview.vue`).
 *
 * **Kein Edge-Auto-Scroll** (noAutoScroll-Invariante: im OSMD-Container scrollt nur der Nutzer).
 * Mausrad-Scrollen während des Drags funktioniert: die Startecke ist in CONTENT-Koordinaten des
 * Scrollers gemerkt (klebt am Notenbild), der Scroll-Listener des Orchestrators ruft
 * {@link refreshDragFrame}.
 */
import {ref, onScopeDispose, type Ref} from 'vue';
import type {GraphicalNote} from 'opensheetmusicdisplay';

import {noteKey} from '@/composables/musicXmlDom';
import {getNoteInfoFromGraphicalNote} from './graphicalNoteInfo';
import {exceedsDragThreshold, normalizedRect} from './selectionLogic';
import type {ScoreHitTesting} from './useScoreHitTesting';
import type {ScoreHighlight} from './useScoreHighlight';
import type {DragSelectionHit} from './useScoreSelection';

/** Client-Pixel Zeigerbewegung, ab der ein gedrückter Zeiger als Rechteck-Drag gilt. Darunter
 *  bleibt die Geste der normale Klickpfad – auch Doppelklick (Menü) bleibt so unberührt. */
const DRAG_THRESHOLD_PX = 5;

/** Rechteck in Content-Koordinaten des Scrollers – direkt als absolute Position des
 *  Rechteck-Divs verwendbar (das Div ist Kind des Scrollers und scrollt mit dem Inhalt). */
export interface DragRect {
    left: number;
    top: number;
    width: number;
    height: number;
}

export interface ScoreDragSelectionDeps {
    hitTesting: ScoreHitTesting;
    highlight: ScoreHighlight;
    containerRef: Ref<HTMLElement | null>;
    scrollContainerRef: Ref<HTMLElement | null>;
    /** Commit der Geste – einziger Selektions-Schreibpfad (in `useScoreSelection`). */
    applyDragSelection: (hits: DragSelectionHit[], opts: {additive: boolean}) => void;
    /** Stellt nach einem Abbruch die Färbung der tatsächlichen Auswahl wieder her. */
    restoreSelectionHighlight: () => void;
}

export function useScoreDragSelection(deps: ScoreDragSelectionDeps) {
    const {hitTesting, highlight, containerRef, scrollContainerRef} = deps;

    const isDragSelecting = ref(false);
    const dragRect = ref<DragRect | null>(null);

    // Gedrückter, aber noch nicht als Drag bestätigter Zeiger (unter dem Schwellwert).
    let armedPointerId: number | null = null;
    let startClientX = 0;
    let startClientY = 0;
    // Startecke in Content-Koordinaten (Client + Scroll-Offset beim Start) – klebt am Notenbild.
    let startContentX = 0;
    let startContentY = 0;
    let lastClientX = 0;
    let lastClientY = 0;
    let lastHits: DragSelectionHit[] = [];
    let lastHitsKey: string | null = null;
    let frameHandle: number | null = null;
    let suppressNextClick = false;

    function scroller(): HTMLElement | null {
        return scrollContainerRef.value;
    }

    function contentPoint(clientX: number, clientY: number): {x: number; y: number} | null {
        const el = scroller();
        if (!el) return null;
        const rect = el.getBoundingClientRect();
        return {x: clientX - rect.left + el.scrollLeft, y: clientY - rect.top + el.scrollTop};
    }

    function onEscape(event: KeyboardEvent): void {
        if (event.key === 'Escape') cancelDrag();
    }

    function resetGesture(): void {
        if (frameHandle !== null) cancelAnimationFrame(frameHandle);
        frameHandle = null;
        window.removeEventListener('keydown', onEscape, true);
        if (armedPointerId !== null) {
            try {
                containerRef.value?.releasePointerCapture?.(armedPointerId);
            } catch {
                // Capture kann schon weg sein (pointercancel) – egal.
            }
        }
        armedPointerId = null;
        isDragSelecting.value = false;
        dragRect.value = null;
        lastHits = [];
        lastHitsKey = null;
    }

    /** Bricht einen laufenden Drag ohne Auswahl-Commit ab (Esc, Rechtsklick, Re-Render,
     *  pointercancel, Unmount). Die bestehende Auswahl bleibt unverändert. */
    function cancelDrag(): void {
        if (armedPointerId === null) return;
        const wasDragging = isDragSelecting.value;
        resetGesture();
        if (wasDragging) {
            highlight.clearHoverColor();
            deps.restoreSelectionHighlight();
            // Auch nach einem Abbruch feuert das mouseup noch ein click – schlucken.
            suppressNextClick = true;
        }
    }

    /** Berechnet Rechteck + Treffer aus der letzten Zeigerposition (ein Lauf pro Frame; ein
     *  direkter Aufruf – pointerup – verwirft einen noch anhängigen Frame). */
    function updateDragFrame(): void {
        if (frameHandle !== null) cancelAnimationFrame(frameHandle);
        frameHandle = null;
        if (!isDragSelecting.value) return;
        const el = scroller();
        const current = contentPoint(lastClientX, lastClientY);
        if (!el || !current) return;

        const content = normalizedRect(startContentX, startContentY, current.x, current.y);
        dragRect.value = {
            left: content.left,
            top: content.top,
            width: content.right - content.left,
            height: content.bottom - content.top,
        };

        // Content → Client (Startecke wandert beim Scrollen mit dem Inhalt).
        const rect = el.getBoundingClientRect();
        const clientRect = {
            left: content.left - el.scrollLeft + rect.left,
            top: content.top - el.scrollTop + rect.top,
            right: content.right - el.scrollLeft + rect.left,
            bottom: content.bottom - el.scrollTop + rect.top,
        };

        const hits: DragSelectionHit[] = [];
        const keys: string[] = [];
        const seen = new Set<string>();
        for (const gNote of hitTesting.notesInClientRect(clientRect)) {
            const info = getNoteInfoFromGraphicalNote(gNote as GraphicalNote);
            if (!info) continue;
            const key = noteKey(info);
            if (seen.has(key)) continue;
            seen.add(key);
            hits.push({info, gNote: gNote as GraphicalNote});
            keys.push(key);
        }
        lastHits = hits;

        // Live-Vorschau nur bei geänderter Treffermenge neu färben (Frame-Dedup wie beim Hover).
        const hitsKey = keys.join('|');
        if (hitsKey !== lastHitsKey) {
            lastHitsKey = hitsKey;
            highlight.clearHoverColor();
            highlight.highlightHoverNotes(hits.map(h => h.gNote));
        }
    }

    function scheduleFrame(): void {
        if (frameHandle !== null) return;
        frameHandle = requestAnimationFrame(updateDragFrame);
    }

    function handleContainerPointerDown(event: PointerEvent): void {
        // Nur Maus, nur Primärtaste: Touch-Ziehen soll Scrollen bleiben, und ein Sekundär-Klick
        // während einer laufenden Geste bricht sie ab statt eine zweite zu starten.
        if (event.button !== 0) {
            cancelDrag();
            return;
        }
        if (event.pointerType !== 'mouse' || armedPointerId !== null) return;
        const start = contentPoint(event.clientX, event.clientY);
        if (!start) return;
        armedPointerId = event.pointerId;
        startClientX = lastClientX = event.clientX;
        startClientY = lastClientY = event.clientY;
        startContentX = start.x;
        startContentY = start.y;
    }

    function handleContainerPointerMove(event: PointerEvent): void {
        if (armedPointerId !== event.pointerId) return;
        lastClientX = event.clientX;
        lastClientY = event.clientY;
        if (!isDragSelecting.value) {
            if (!exceedsDragThreshold(startClientX, startClientY, event.clientX, event.clientY, DRAG_THRESHOLD_PX)) {
                return;
            }
            isDragSelecting.value = true;
            try {
                containerRef.value?.setPointerCapture?.(event.pointerId);
            } catch {
                // Ohne Capture funktioniert die Geste innerhalb des Containers trotzdem.
            }
            window.addEventListener('keydown', onEscape, true);
        }
        // Primärtaste unterwegs verloren (mouseup außerhalb ohne Capture, OS-Eigenheiten) → Abbruch.
        if ((event.buttons & 1) === 0) {
            cancelDrag();
            return;
        }
        scheduleFrame();
    }

    function handleContainerPointerUp(event: PointerEvent): void {
        if (armedPointerId !== event.pointerId) return;
        if (!isDragSelecting.value) {
            // Unter dem Schwellwert: normaler Klick, der Klickpfad übernimmt.
            resetGesture();
            return;
        }
        // Letzten Stand synchron einrechnen (das jüngste pointermove kann noch im rAF hängen).
        lastClientX = event.clientX;
        lastClientY = event.clientY;
        updateDragFrame();
        const hits = lastHits;
        const additive = event.ctrlKey || event.metaKey;
        resetGesture();
        highlight.clearHoverColor();
        deps.applyDragSelection(hits, {additive});
        suppressNextClick = true;
    }

    /** One-Shot: `true` genau einmal nach einem abgeschlossenen/abgebrochenen Drag – der Host
     *  schluckt damit den Folge-`click` des mouseup (der die frische Auswahl überschriebe). */
    function consumeDragClick(): boolean {
        const consume = suppressNextClick;
        suppressNextClick = false;
        return consume;
    }

    /** Für den Scroll-Listener des Orchestrators: Rechteck + Treffer zur unveränderten
     *  Zeigerposition neu berechnen (die Content-Startecke ist mit dem Inhalt gewandert). */
    function refreshDragFrame(): void {
        if (isDragSelecting.value) scheduleFrame();
    }

    onScopeDispose(() => cancelDrag());

    return {
        isDragSelecting,
        dragRect,
        handleContainerPointerDown,
        handleContainerPointerMove,
        handleContainerPointerUp,
        cancelDrag,
        consumeDragClick,
        refreshDragFrame,
    } as const;
}

export type ScoreDragSelection = ReturnType<typeof useScoreDragSelection>;
